// src/core/map.js
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-rotate";

// Fix default marker icon paths when bundling (Vite)
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

const MAP_MODE_KEY = "hydrotools:map-mode";

const BASEMAPS = {
  light: {
    // Minimal "ghost" canvas basemap with continental/coastline context and very low clutter.
    url: "https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    options: { maxNativeZoom: 16, maxZoom: 24, attribution: "Tiles &copy; Esri", crossOrigin: true },
  },
  dark: {
    // Dark minimal canvas counterpart for map-mode parity.
    url: "https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    options: { maxNativeZoom: 16, maxZoom: 24, attribution: "Tiles &copy; Esri", crossOrigin: true },
  },
};

export function createMap({ mapEl, events }) {
  // Set default icon URLs (otherwise markers can appear broken)
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
  });

  const map = L.map(mapEl, {
    zoomControl: true,
    preferCanvas: true,
    maxZoom: 24,
    zoomSnap: 0.25,
    rotate: true,
    bearing: 0,
    rotateControl: false,
    touchRotate: true,
  }).setView([37.78, -122.45], 10);

  let activeBasemapLayer = null;
  const measureLayer = L.featureGroup().addTo(map);
  const measureDraft = L.featureGroup().addTo(map);
  let measureMode = "none"; // none | length | area
  let measureVerts = [];
  let measureControlEl = null;
  let measureStatusEl = null;
  let searchControlEl = null;
  let searchExpanded = false;
  let searchBusy = false;
  let searchSuggestTimer = null;
  let searchSuggesting = false;
  let searchSuggestItems = [];
  let searchSuggestIndex = -1;
  let rotateControlEl = null;
  let rotateExpanded = false;
  let fineWheelAccumulator = 0;
  let fineWheelQueuedSteps = 0;
  let fineWheelFlushTimer = null;

  function formatLength(meters) {
    const m = Number(meters) || 0;
    const nm = m / 1852;
    if (m < 1000) return `${m.toFixed(1)} m (${nm.toFixed(3)} nm)`;
    return `${(m / 1000).toFixed(3)} km (${nm.toFixed(3)} nm)`;
  }

  function ringAreaSqMeters(latlngs) {
    if (!Array.isArray(latlngs) || latlngs.length < 3) return 0;
    // Spherical excess approximation used by common web mapping tools.
    const R = 6378137;
    let sum = 0;
    for (let i = 0; i < latlngs.length; i += 1) {
      const p1 = latlngs[i];
      const p2 = latlngs[(i + 1) % latlngs.length];
      const lon1 = (p1.lng * Math.PI) / 180;
      const lon2 = (p2.lng * Math.PI) / 180;
      const lat1 = (p1.lat * Math.PI) / 180;
      const lat2 = (p2.lat * Math.PI) / 180;
      sum += (lon2 - lon1) * (2 + Math.sin(lat1) + Math.sin(lat2));
    }
    return Math.abs((sum * R * R) / 2);
  }

  function formatArea(areaM2) {
    const a = Number(areaM2) || 0;
    const sqNm = a / (1852 * 1852);
    if (a < 1_000_000) return `${a.toFixed(1)} m² (${sqNm.toFixed(4)} sq nm)`;
    return `${(a / 1_000_000).toFixed(3)} km² (${sqNm.toFixed(4)} sq nm)`;
  }

  function sketchLength(latlngs) {
    let m = 0;
    for (let i = 1; i < latlngs.length; i += 1) m += map.distance(latlngs[i - 1], latlngs[i]);
    return m;
  }

  function setMeasureStatus(txt) {
    if (!measureStatusEl) return;
    measureStatusEl.textContent = String(txt || "");
  }

  function updateMeasureButtons() {
    if (!measureControlEl) return;
    measureControlEl.querySelectorAll("[data-measure-mode]").forEach((btn) => {
      const active = btn.getAttribute("data-measure-mode") === measureMode;
      btn.classList.toggle("active", active);
      btn.setAttribute("aria-pressed", active ? "true" : "false");
    });
  }

  function clearDraft() {
    measureVerts = [];
    measureDraft.clearLayers();
  }

  function clearMeasurements() {
    clearDraft();
    measureLayer.clearLayers();
    setMeasureStatus("Ready");
  }

  function redrawDraft() {
    measureDraft.clearLayers();
    if (!measureVerts.length || measureMode === "none") return;
    const style = { color: "#22d3ee", weight: 2, opacity: 0.95, dashArray: "5 5" };
    if (measureMode === "length") {
      L.polyline(measureVerts, style).addTo(measureDraft);
      if (measureVerts.length >= 2) setMeasureStatus(`Length: ${formatLength(sketchLength(measureVerts))}`);
      else setMeasureStatus("Length: click to add points, double-click to finish");
    } else if (measureMode === "area") {
      L.polygon(measureVerts, { ...style, fillOpacity: 0.08 }).addTo(measureDraft);
      if (measureVerts.length >= 3) setMeasureStatus(`Area: ${formatArea(ringAreaSqMeters(measureVerts))}`);
      else setMeasureStatus("Area: click to add vertices, double-click to finish");
    }
    measureVerts.forEach((ll) => L.circleMarker(ll, { radius: 3, color: "#22d3ee", weight: 2, fillOpacity: 1 }).addTo(measureDraft));
  }

  function finalizeMeasurement() {
    if (measureMode === "length") {
      if (measureVerts.length < 2) { setMeasureStatus("Need at least 2 points"); clearDraft(); return; }
      const len = sketchLength(measureVerts);
      L.polyline(measureVerts, { color: "#f59e0b", weight: 3, opacity: 0.95 }).addTo(measureLayer);
      setMeasureStatus(`Length saved: ${formatLength(len)}`);
      clearDraft();
      return;
    }
    if (measureMode === "area") {
      if (measureVerts.length < 3) { setMeasureStatus("Need at least 3 vertices"); clearDraft(); return; }
      const area = ringAreaSqMeters(measureVerts);
      L.polygon(measureVerts, { color: "#f59e0b", weight: 2.5, fillOpacity: 0.14 }).addTo(measureLayer);
      setMeasureStatus(`Area saved: ${formatArea(area)}`);
      clearDraft();
    }
  }

  function setMeasureMode(mode) {
    measureMode = mode === "length" || mode === "area" ? mode : "none";
    clearDraft();
    if (measureMode === "none") {
      map.getContainer().style.cursor = "";
      map.doubleClickZoom.enable();
      setMeasureStatus("Ready");
    } else {
      map.getContainer().style.cursor = "crosshair";
      map.doubleClickZoom.disable();
      setMeasureStatus(measureMode === "length" ? "Length: click to add points" : "Area: click to add vertices");
    }
    updateMeasureButtons();
  }

  function ensureMeasureControl() {
    if (!mapEl?.parentElement || measureControlEl) return;
    measureControlEl = document.createElement("div");
    measureControlEl.className = "map-measure-control";
    measureControlEl.innerHTML = `
      <button type="button" class="map-measure-btn" data-measure-mode="length" title="Measure length" aria-label="Measure length">
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
          <path d="M6 18 18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/>
          <circle cx="6" cy="18" r="2.1" fill="currentColor"/>
          <circle cx="18" cy="6" r="2.1" fill="currentColor"/>
          <path d="M10.2 13.8h2.1M12.1 11.9v2.1M13.8 10.2h2.1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"/>
        </svg>
      </button>
      <button type="button" class="map-measure-btn map-measure-area-btn" data-measure-mode="area" title="Measure area" aria-label="Measure area">
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
          <path d="M6 15 9 6.5 17.5 7 19 14.5 12 18Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" fill="none"/>
          <circle cx="6" cy="15" r="1.5" fill="currentColor"/>
          <circle cx="9" cy="6.5" r="1.5" fill="currentColor"/>
          <circle cx="17.5" cy="7" r="1.5" fill="currentColor"/>
          <circle cx="19" cy="14.5" r="1.5" fill="currentColor"/>
          <circle cx="12" cy="18" r="1.5" fill="currentColor"/>
        </svg>
      </button>
      <button type="button" class="map-measure-btn" data-measure-clear="1" title="Clear measurements" aria-label="Clear measurements">
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d="M6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41 17.59 5 12 10.59 6.41 5Z"/></svg>
      </button>
      <div id="map-measure-status" class="map-measure-status" aria-live="polite"></div>
    `;
    measureControlEl.addEventListener("click", (e) => {
      const modeBtn = e.target?.closest?.("[data-measure-mode]");
      if (modeBtn) {
        const mode = modeBtn.getAttribute("data-measure-mode");
        setMeasureMode(measureMode === mode ? "none" : mode);
        return;
      }
      if (e.target?.closest?.("[data-measure-clear]")) {
        clearMeasurements();
      }
    });
    mapEl.parentElement.appendChild(measureControlEl);
    measureStatusEl = measureControlEl.querySelector("#map-measure-status");
    updateMeasureButtons();
    setMeasureStatus("Ready");
  }

  function setSearchExpanded(next) {
    searchExpanded = !!next;
    if (!searchControlEl) return;
    searchControlEl.dataset.expanded = searchExpanded ? "true" : "false";
    const input = searchControlEl.querySelector("#map-search-input");
    if (searchExpanded) {
      setTimeout(() => {
        input?.focus?.();
        input?.select?.();
      }, 40);
    }
  }

  function setSearchBusy(next, status = "") {
    searchBusy = !!next;
    if (!searchControlEl) return;
    searchControlEl.dataset.busy = searchBusy ? "true" : "false";
    const statusEl = searchControlEl.querySelector("#map-search-status");
    if (statusEl) statusEl.textContent = status || "";
  }

  function parseNominatimBounds(row) {
    const b = Array.isArray(row?.boundingbox) ? row.boundingbox : null;
    if (!b || b.length < 4) return null;
    const south = Number(b[0]);
    const north = Number(b[1]);
    const west = Number(b[2]);
    const east = Number(b[3]);
    if (![south, north, west, east].every(Number.isFinite)) return null;
    const bounds = L.latLngBounds([south, west], [north, east]);
    return bounds.isValid() ? bounds : null;
  }

  function parsePhotonBounds(feature) {
    const extent = Array.isArray(feature?.properties?.extent) ? feature.properties.extent : null;
    if (!extent || extent.length < 4) return null;
    const west = Number(extent[0]);
    const south = Number(extent[1]);
    const east = Number(extent[2]);
    const north = Number(extent[3]);
    if (![south, north, west, east].every(Number.isFinite)) return null;
    const bounds = L.latLngBounds([south, west], [north, east]);
    return bounds.isValid() ? bounds : null;
  }

  function clearSearchSuggestions() {
    searchSuggestItems = [];
    searchSuggestIndex = -1;
    const list = searchControlEl?.querySelector?.("#map-search-suggestions");
    if (list) {
      list.innerHTML = "";
      list.hidden = true;
    }
  }

  function renderSearchSuggestions(items) {
    searchSuggestItems = Array.isArray(items) ? items : [];
    searchSuggestIndex = -1;
    const list = searchControlEl?.querySelector?.("#map-search-suggestions");
    if (!list) return;
    if (!searchSuggestItems.length) {
      list.innerHTML = "";
      list.hidden = true;
      return;
    }
    list.innerHTML = searchSuggestItems
      .map((item, idx) => `<button type="button" class="map-search-suggestion" data-suggest-index="${idx}" title="${String(item.label || "").replace(/"/g, "&quot;")}">${item.label}</button>`)
      .join("");
    list.hidden = false;
  }

  function updateSearchSuggestionHighlight() {
    const list = searchControlEl?.querySelector?.("#map-search-suggestions");
    if (!list) return;
    list.querySelectorAll(".map-search-suggestion").forEach((el, idx) => {
      el.classList.toggle("active", idx === searchSuggestIndex);
    });
  }

  function zoomToSearchResult(result) {
    if (!result) return;
    if (result?.bounds) map.fitBounds(result.bounds.pad(0.2));
    else if (Number.isFinite(result?.lat) && Number.isFinite(result?.lon)) map.setView([result.lat, result.lon], Math.max(map.getZoom(), 12));
  }

  async function suggestPlaces(query) {
    const q = String(query || "").trim();
    if (q.length < 2) return [];
    const encoded = encodeURIComponent(q);
    const timeoutMs = 5000;
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), timeoutMs);
    try {
      const url = `https://photon.komoot.io/api/?q=${encoded}&limit=5`;
      const r = await fetch(url, { signal: ctl.signal, headers: { Accept: "application/json" } });
      if (!r.ok) return [];
      const data = await r.json();
      const feats = Array.isArray(data?.features) ? data.features : [];
      return feats.map((ft) => {
        const c = ft?.geometry?.coordinates;
        const lon = Number(Array.isArray(c) ? c[0] : NaN);
        const lat = Number(Array.isArray(c) ? c[1] : NaN);
        const name = String(ft?.properties?.name || "").trim();
        const city = String(ft?.properties?.city || ft?.properties?.state || ft?.properties?.country || "").trim();
        const label = city && city !== name ? `${name}, ${city}` : name || q;
        return {
          lat,
          lon,
          label,
          bounds: parsePhotonBounds(ft),
        };
      }).filter((it) => Number.isFinite(it.lat) && Number.isFinite(it.lon));
    } catch {
      return [];
    } finally {
      clearTimeout(timer);
    }
  }

  async function geocodePlace(query) {
    const q = String(query || "").trim();
    if (!q) return null;
    const encoded = encodeURIComponent(q);
    const timeoutMs = 10000;
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), timeoutMs);
    try {
      const primary = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encoded}`;
      const r1 = await fetch(primary, { signal: ctl.signal, headers: { Accept: "application/json" } });
      if (r1.ok) {
        const rows = await r1.json();
        if (Array.isArray(rows) && rows.length) {
          const row = rows[0];
          const lat = Number(row.lat);
          const lon = Number(row.lon);
          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            return {
              lat,
              lon,
              label: String(row.display_name || q),
              bounds: parseNominatimBounds(row),
            };
          }
        }
      }
    } catch {}
    try {
      const fallback = `https://photon.komoot.io/api/?q=${encoded}&limit=1`;
      const r2 = await fetch(fallback, { signal: ctl.signal, headers: { Accept: "application/json" } });
      if (r2.ok) {
        const data = await r2.json();
        const ft = Array.isArray(data?.features) ? data.features[0] : null;
        const c = ft?.geometry?.coordinates;
        const lon = Number(Array.isArray(c) ? c[0] : NaN);
        const lat = Number(Array.isArray(c) ? c[1] : NaN);
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          return {
            lat,
            lon,
            label: String(ft?.properties?.name || q),
            bounds: null,
          };
        }
      }
    } catch {}
    finally {
      clearTimeout(timer);
    }
    return null;
  }

  function ensureSearchControl() {
    if (!mapEl?.parentElement || searchControlEl) return;
    searchControlEl = document.createElement("div");
    searchControlEl.className = "map-search-control";
    searchControlEl.dataset.expanded = "false";
    searchControlEl.dataset.busy = "false";
    searchControlEl.innerHTML = `
      <div class="map-search-shell">
        <button type="button" id="map-search-toggle" class="map-search-btn" title="Search place" aria-label="Search place" aria-expanded="false">
          <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16a6.47 6.47 0 0 0 4.23-1.57l.27.28v.79L20 21.5 21.5 20l-6-6Zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14Z"/></svg>
        </button>
        <form id="map-search-form" class="map-search-form" autocomplete="off">
          <input id="map-search-input" type="text" placeholder="Search place..." />
          <div id="map-search-suggestions" class="map-search-suggestions" hidden></div>
        </form>
      </div>
      <div id="map-search-status" class="map-search-status" aria-live="polite"></div>
    `;

    searchControlEl.addEventListener("click", (e) => {
      const toggle = e.target?.closest?.("#map-search-toggle");
      if (!toggle) return;
      const next = !searchExpanded;
      setSearchExpanded(next);
      toggle.setAttribute("aria-expanded", next ? "true" : "false");
      if (!next) {
        setSearchBusy(false, "");
        clearSearchSuggestions();
      }
    });

    const form = searchControlEl.querySelector("#map-search-form");
    const input = searchControlEl.querySelector("#map-search-input");
    form?.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (searchSuggestIndex >= 0 && searchSuggestItems[searchSuggestIndex]) {
        const picked = searchSuggestItems[searchSuggestIndex];
        zoomToSearchResult(picked);
        setSearchBusy(false, picked.label);
        input.value = picked.label;
        clearSearchSuggestions();
        return;
      }
      const query = String(input?.value || "").trim();
      if (!query || searchBusy) return;
      setSearchBusy(true, "Searching...");
      const result = await geocodePlace(query);
      zoomToSearchResult(result);
      setSearchBusy(false, result ? result.label : "No results.");
      clearSearchSuggestions();
    });

    input?.addEventListener("input", () => {
      const q = String(input?.value || "").trim();
      if (searchSuggestTimer) clearTimeout(searchSuggestTimer);
      if (!q || q.length < 2) {
        clearSearchSuggestions();
        return;
      }
      searchSuggestTimer = setTimeout(async () => {
        if (searchSuggesting) return;
        searchSuggesting = true;
        const items = await suggestPlaces(q);
        searchSuggesting = false;
        const current = String(input?.value || "").trim();
        if (current !== q || !searchExpanded) return;
        renderSearchSuggestions(items);
      }, 220);
    });

    input?.addEventListener("keydown", (e) => {
      if (e.key === "ArrowDown" && searchSuggestItems.length) {
        e.preventDefault();
        searchSuggestIndex = Math.min(searchSuggestItems.length - 1, searchSuggestIndex + 1);
        updateSearchSuggestionHighlight();
        return;
      }
      if (e.key === "ArrowUp" && searchSuggestItems.length) {
        e.preventDefault();
        searchSuggestIndex = Math.max(0, searchSuggestIndex - 1);
        updateSearchSuggestionHighlight();
        return;
      }
      if (e.key === "Enter" && searchSuggestIndex >= 0 && searchSuggestItems[searchSuggestIndex]) {
        e.preventDefault();
        const picked = searchSuggestItems[searchSuggestIndex];
        input.value = picked.label;
        zoomToSearchResult(picked);
        setSearchBusy(false, picked.label);
        clearSearchSuggestions();
        return;
      }
      if (e.key === "Escape") {
        setSearchExpanded(false);
        searchControlEl.querySelector("#map-search-toggle")?.setAttribute("aria-expanded", "false");
        setSearchBusy(false, "");
        clearSearchSuggestions();
      }
    });

    searchControlEl.addEventListener("mousedown", (e) => {
      const btn = e.target?.closest?.(".map-search-suggestion");
      if (!btn) return;
      e.preventDefault();
      const idx = Number(btn.getAttribute("data-suggest-index"));
      if (!Number.isFinite(idx) || !searchSuggestItems[idx]) return;
      const picked = searchSuggestItems[idx];
      input.value = picked.label;
      zoomToSearchResult(picked);
      setSearchBusy(false, picked.label);
      clearSearchSuggestions();
    });

    document.addEventListener("mousedown", (e) => {
      if (!searchExpanded) return;
      if (searchControlEl?.contains?.(e.target)) return;
      setSearchExpanded(false);
      searchControlEl.querySelector("#map-search-toggle")?.setAttribute("aria-expanded", "false");
      setSearchBusy(false, "");
      clearSearchSuggestions();
    });

    mapEl.parentElement.appendChild(searchControlEl);
  }

  function getBearingDeg() {
    const raw = Number(map?.getBearing?.());
    if (!Number.isFinite(raw)) return 0;
    const norm = raw % 360;
    return norm < 0 ? norm + 360 : norm;
  }

  function setBearingDeg(next) {
    if (typeof map?.setBearing !== "function") return;
    map.setBearing(Number(next) || 0);
    updateRotateUI();
  }

  function setRotateExpanded(next) {
    rotateExpanded = !!next;
    if (!rotateControlEl) return;
    rotateControlEl.dataset.expanded = rotateExpanded ? "true" : "false";
    const toggle = rotateControlEl.querySelector("#map-rotate-toggle");
    if (toggle) {
      toggle.setAttribute("aria-expanded", rotateExpanded ? "true" : "false");
      toggle.classList.toggle("active", rotateExpanded);
    }
  }

  function updateRotateUI() {
    if (!rotateControlEl) return;
    const bearing = getBearingDeg();
    const slider = rotateControlEl.querySelector("#map-rotate-slider");
    const status = rotateControlEl.querySelector("#map-rotate-status");
    if (slider && document.activeElement !== slider) slider.value = String(Math.round(bearing));
    if (status) status.textContent = `${bearing.toFixed(0)} deg`;
  }

  function ensureRotateControl() {
    if (!mapEl?.parentElement || rotateControlEl) return;
    rotateControlEl = document.createElement("div");
    rotateControlEl.className = "map-rotate-control";
    rotateControlEl.dataset.expanded = "false";
    rotateControlEl.innerHTML = `
      <div class="map-rotate-shell">
        <button type="button" id="map-rotate-toggle" class="map-measure-btn map-rotate-btn" title="Rotate map" aria-label="Rotate map" aria-expanded="false">
          <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d="M12 2v3.2a6.8 6.8 0 1 1-6.8 6.8H2a10 10 0 1 0 10-10Zm-1.8.6L6.8 6h2.6v4h2.2V6h2.6l-3.4-3.4Z"/></svg>
        </button>
        <button type="button" class="map-measure-btn map-rotate-btn map-rotate-reset" data-rotate-action="reset" title="Reset north-up" aria-label="Reset north-up">
          <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d="m12 2 6 6h-4v6h-4V8H6l6-6Zm-7 15h14v2H5v-2Z"/></svg>
        </button>
        <div class="map-rotate-panel">
          <div class="map-rotate-slider-wrap">
            <input id="map-rotate-slider" class="map-rotate-slider" type="range" min="0" max="360" step="1" value="0" aria-label="Map bearing in degrees" />
          </div>
        </div>
      </div>
      <div id="map-rotate-status" class="map-rotate-status" aria-live="polite">0 deg</div>
    `;

    rotateControlEl.querySelector("#map-rotate-toggle")?.addEventListener("click", () => {
      setRotateExpanded(!rotateExpanded);
    });

    rotateControlEl.querySelector("#map-rotate-slider")?.addEventListener("input", (e) => {
      const value = Number(e?.target?.value);
      if (!Number.isFinite(value)) return;
      setBearingDeg(value);
      const status = rotateControlEl.querySelector("#map-rotate-status");
      if (status) status.textContent = `${Math.round(value)} deg`;
    });

    rotateControlEl.querySelector("#map-rotate-slider")?.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      setRotateExpanded(false);
    });

    rotateControlEl.querySelector("[data-rotate-action=\"reset\"]")?.addEventListener("click", () => {
      setBearingDeg(0);
    });

    document.addEventListener("mousedown", (e) => {
      if (!rotateExpanded) return;
      if (rotateControlEl?.contains?.(e.target)) return;
      setRotateExpanded(false);
    });

    map.on("rotate", updateRotateUI);
    mapEl.parentElement.appendChild(rotateControlEl);
    updateRotateUI();
  }

  function applyFineWheelZoom(event) {
    const deltaY = Number(event?.deltaY || 0);
    if (!Number.isFinite(deltaY) || deltaY === 0) return;
    const scale = event.deltaMode === 1 ? 18 : event.deltaMode === 2 ? 120 : 1;
    fineWheelAccumulator += deltaY * scale;
    const threshold = 120;
    if (Math.abs(fineWheelAccumulator) < threshold) return;
    const direction = fineWheelAccumulator > 0 ? -1 : 1;
    fineWheelAccumulator = 0;
    fineWheelQueuedSteps += direction;
    const rect = map.getContainer().getBoundingClientRect();
    const point = L.point(
      Number(event.clientX) - rect.left,
      Number(event.clientY) - rect.top,
    );
    if (fineWheelFlushTimer) clearTimeout(fineWheelFlushTimer);
    fineWheelFlushTimer = setTimeout(() => {
      const queued = fineWheelQueuedSteps;
      fineWheelQueuedSteps = 0;
      fineWheelFlushTimer = null;
      if (!queued) return;
      const currentZoom = Number(map.getZoom?.() || 0);
      const nextZoom = Math.max(map.getMinZoom(), Math.min(map.getMaxZoom(), currentZoom + (queued * 0.25)));
      map.setZoomAround(point, nextZoom, { animate: false });
    }, 90);
  }

  map.on("click", (e) => {
    if (measureMode === "none") return;
    measureVerts.push(e.latlng);
    redrawDraft();
  });

  map.on("dblclick", (e) => {
    if (measureMode === "none") return;
    L.DomEvent.stop(e);
    finalizeMeasurement();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    if (measureMode === "none") return;
    clearDraft();
    setMeasureStatus("Sketch cancelled");
  });

  map.getContainer().addEventListener("wheel", (e) => {
    if (!e.ctrlKey) {
      fineWheelAccumulator = 0;
      fineWheelQueuedSteps = 0;
      if (fineWheelFlushTimer) {
        clearTimeout(fineWheelFlushTimer);
        fineWheelFlushTimer = null;
      }
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    applyFineWheelZoom(e);
  }, { passive: false, capture: true });

  function setBasemap(mode) {
    const nextMode = mode === "dark" ? "dark" : "light";
    const def = BASEMAPS[nextMode];
    if (!def) return;
    if (activeBasemapLayer) map.removeLayer(activeBasemapLayer);
    activeBasemapLayer = L.tileLayer(def.url, def.options).addTo(map);
  }

  const savedMode = localStorage.getItem(MAP_MODE_KEY);
  setBasemap(savedMode === "dark" ? "dark" : "light");
  ensureMeasureControl();
  ensureSearchControl();
  ensureRotateControl();

  events.on("map:basemap:mode", ({ mode } = {}) => {
    setBasemap(mode);
  });

  // If the container was sized after init, Leaflet needs invalidateSize()
  setTimeout(() => map.invalidateSize(), 200);
  window.addEventListener("resize", () => {
    requestAnimationFrame(() => map.invalidateSize({ pan: false, debounceMoveend: true }));
  });

  events.emit("map:ready", { map });
  return map;
}



